import { Injectable } from "@angular/core";
import { VpNotification } from "./notification.models";

const STORAGE_KEY_PREFIX = "vp.notifications.";

/**
 * Per-user persistence for the notification tray, so call lights survive a reload. Only
 * call lights persist: other kinds can carry patient names, and the stored tray outlives
 * logout on shared workstations. A read that drops anything rewrites the stored value, so
 * entries saved under older rules don't linger even if the tray never changes again.
 */
@Injectable({ providedIn: "root" })
export class NotificationTrayStorage {
  read(userId: string): VpNotification[] {
    const key = STORAGE_KEY_PREFIX + userId;
    try {
      const raw = localStorage.getItem(key);
      if (raw === null) {
        return [];
      }
      const parsed = parseJson(raw);
      if (!Array.isArray(parsed)) {
        localStorage.removeItem(key);
        return [];
      }
      // Validate item by item: one corrupt entry shouldn't cost the user the whole tray.
      const kept = parsed.filter(isStoredNotification).filter(persists);
      if (kept.length !== parsed.length) {
        localStorage.setItem(key, JSON.stringify(kept));
      }
      return kept;
    } catch {
      return [];
    }
  }

  write(userId: string, items: readonly VpNotification[]): void {
    try {
      localStorage.setItem(STORAGE_KEY_PREFIX + userId, JSON.stringify(items.filter(persists)));
    } catch {
      /* storage unavailable or over quota — the tray just won't survive a reload */
    }
  }
}

function parseJson(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
}

function persists(n: VpNotification): boolean {
  return n.kind === "call-light";
}

function isStoredNotification(value: unknown): value is VpNotification {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const item = value as Record<string, unknown>;
  return (
    typeof item["id"] === "string" &&
    typeof item["kind"] === "string" &&
    typeof item["tone"] === "string" &&
    typeof item["heading"] === "string" &&
    typeof item["resolved"] === "boolean" &&
    typeof item["createdAt"] === "number" &&
    Number.isFinite(item["createdAt"])
  );
}
